import React, { useEffect, useState } from "react";
import { Box, Flex, useToast } from "@chakra-ui/react";
import CTACardWithConfirmation from "../../../UI/CTACardWithConfirmation";
import CollapsibleInformation from "../../../UI/CollapsibleInformation";
import { TConfirmation } from "../types";
import walletService from "../../../../Services/WalletService";
import { tokenTypes } from "../../../../Utils/Types";
import { toastError, toastSuccess } from "../../../../config/toast";
import config from "../../../../config/config.json";
import { useWeb3 } from "@chainsafe/web3-context";
import DAI from "../../../../media/tokens/dai.svg";

const configurations = (networkId: any, tokenType: tokenTypes) => {
  const network = config.chains.find(
    (network) => network.networkId === networkId
  );
  const token =
    network && network.tokens.find((token) => token.symbol === tokenType);
  return [
    {
      name: token && token.name,
      type: token && token.name,
      networkId: parseInt(process.env.REACT_APP_PALM_NETWORK_ID as string),
      title: "Add the DAI Token",
      desc: "Click the button below to add the DAI token as a custom token in your wallet.",
      buttonCopy: "Add DAI Token",
      contract: token && token.address,
      symbol: token && token.name,
      precision: 18,
      collapsibleTitle: "Manually add the DAI token instead",
      table: [
        {
          field: "Token Contract Address",
          input: token && token.address,
        },
        {
          field: "Token Symbol",
          input: token && token.name,
        },
        {
          field: "Decimal of Precision",
          input: 18,
        },
      ],
      help: {
        text: "Metamask FAQ: How to view/see your tokens (custom tokens) in Metamask →",
        url: "https://metamask.zendesk.com/hc/en-us/articles/360015489031-How-to-view-see-your-tokens-custom-tokens-in-Metamask",
      },
      check: "I have added the DAI token",
    },
  ];
};

const AddCustomTokens = ({ onConfirmation, step }: TConfirmation) => {
  const { network, switchNetwork } = useWeb3();
  const [confirmed, setConfirmed] = useState(false);
  const [tokenStatus, setTokenStatus] = useState(
    configurations(
      parseInt(process.env.REACT_APP_PALM_NETWORK_ID as string),
      "DAI" as tokenTypes
    ).map((token) => false)
  );
  const [openAddManually, setOpenAddManually] = useState(false);
  const toast = useToast();

  useEffect(() => {
    onConfirmation(step, confirmed);
  }, [confirmed]);

  const tokenConfirmed = (index: number, checked: boolean) => {
    let newTokenStatus = tokenStatus;
    newTokenStatus[index] = checked;
    setTokenStatus(newTokenStatus);
    const enabled = !newTokenStatus.includes(false);
    setConfirmed(enabled);
  };

  const addToken = async (networkId: number, tokenType: tokenTypes) => {
    // Make sure wallet is on the correct network:
    if (networkId !== network) {
      await switchNetwork(networkId);
    }
    const tokenAdded = await walletService.addTokenToWallet(
      networkId,
      tokenType
    );

    if (tokenAdded) {
      toast({
        title: `${tokenType} token succesfully added to wallet.`,
        ...toastSuccess,
      });
      tokenConfirmed(step, true);
    } else {
      toast({
        title: "Something was wrong",
        description: `You can add the ${tokenType} token manually to wallet.`,
        ...toastError,
      });
      setOpenAddManually(!openAddManually);
    }
    return tokenAdded;
  };

  return (
    <Flex>
      {configurations(
        parseInt(process.env.REACT_APP_PALM_NETWORK_ID as string),
        "DAI" as tokenTypes
      ).map((token, index) => (
        <Box key={`Add-token-${token.name}-card`}>
          <CTACardWithConfirmation
            name={token.name}
            title={token.title}
            desc={token.desc}
            buttonCopy={token.buttonCopy}
            buttonOnClick={() =>
              addToken(token.networkId as any, token.type as tokenTypes)
            }
            onConfirmation={(index: number, checked: boolean) =>
              tokenConfirmed(index, checked)
            }
            index={index}
            check={token.check}
            logo={DAI}
            addInformation={
              <CollapsibleInformation
                title={token.collapsibleTitle}
                table={token.table}
                itemName={token.name}
                help={token.help}
                shouldOpen={openAddManually}
              />
            }
          />
        </Box>
      ))}
    </Flex>
  );
};

export default AddCustomTokens;
